<!-- this is a wrapper around an element that needs file upload dropzone support -->
<template>
    <div ref="container" class="relative">
        <template v-if="isUploadEnabled">
            <slot :click-input="handleClickInput"></slot>
            <input
                ref="fileInput"
                type="file"
                aria-hidden
                :accept="acceptedFileTypes ?? fileTypes"
                :multiple="isMultipleFileUploadEnabled"
                :disabled="!canReceiveFiles"
                class="hidden"
                @change="handleFileChange" />
            <Transition name="fade">
                <div
                    v-if="canUploadDocuments"
                    class="pointer-events-none absolute inset-0 flex items-center justify-center gap-4 text-control-accent border-4 border-dashed border-control-accent transition-colors rounded-lgr"
                    :class="[isOverContainerDropzone ? 'bg-dropzone-bg-active' : 'bg-dropzone-bg']">
                    <i class="bi bi-image text-2xl/none" aria-hidden="true" />
                    <p class="body-semibold">{{ t("components.fileUpload.dropHint") }}</p>
                </div>
            </Transition>
        </template>
        <div v-else>
            <BaseNotification theme="error" :text="t('components.fileUpload.notEnabled')" hide-dismiss />
        </div>
    </div>
</template>

<script setup lang="ts">
import { useDropZone } from "@vueuse/core";
import { useFileUpload } from "~vue/composables/useFileUpload";
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import BaseNotification from "./BaseNotification.vue";

const { t } = useI18n();

interface Props {
    overrideDocumentUploadFlag?: boolean;
    isDocumentDropzoneEnabled?: boolean;
    isDisabled?: boolean;
    acceptedFileTypes?: string;
    isMultipleFileUploadEnabled?: boolean;
}

const { overrideDocumentUploadFlag = false, isDocumentDropzoneEnabled = false, isDisabled = false, acceptedFileTypes, isMultipleFileUploadEnabled = false } = defineProps<Props>();

const emit = defineEmits<{
    fileUpload: [files: File[]];
}>();

const { acceptedFileTypes: fileTypes, isUploadEnabled: documentUploadEnabled } = useFileUpload();
const isUploadEnabled = computed(() => overrideDocumentUploadFlag || documentUploadEnabled.value);
const canReceiveFiles = computed(() => isUploadEnabled.value && !isDisabled);

const canUploadDocuments = computed(() => {
    if (!canReceiveFiles.value) {
        return false;
    }
    if (isDocumentDropzoneEnabled) {
        return isOverDocumentBody.value;
    }

    return isOverContainerDropzone.value;
});

const dropZoneContainer = useTemplateRef<HTMLDivElement>("container");
// this is the actual drop area where files are processed on drop
const { isOverDropZone: isOverContainerDropzone } = useDropZone(dropZoneContainer, {
    onDrop: deliverFiles,
    // Accept the drop, then keep its first file in single-file mode instead of rejecting the whole batch.
    multiple: true,
    preventDefaultForUnhandled: true,
});

// this second dropzone triggers on the body element so that the drag an drop area on chat message control can be seen
const { isOverDropZone: isOverDocumentBody } = useDropZone(document.body, {
    preventDefaultForUnhandled: true,
});

const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const handleClickInput = () => {
    if (!canReceiveFiles.value) {
        return;
    }
    fileInput.value?.click();
};

const handleFileChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = Array.from(target.files ?? []);
    // A rejected file must be selectable again without first choosing a different file.
    target.value = "";
    deliverFiles(files);
};

function deliverFiles(files: File[] | null): void {
    if (!canReceiveFiles.value || !files?.length) {
        return;
    }
    emit("fileUpload", isMultipleFileUploadEnabled ? files : files.slice(0, 1));
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 200ms ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
